import { Component, Element, h, Host, Prop } from '@stencil/core';

@Component({
  tag: 'me-image',
  styleUrl: './me-image.scss',
  shadow: false,
})
export class MeImage {
  @Element() host: HTMLElement;

  @Prop() variant: 'normal' | 'person' = 'normal';
  @Prop() theme: 'primary' | 'neutral' = 'primary';

  @Prop() author?: string;
  @Prop() authorSize?: string;
  @Prop() imageBlur = false;
  @Prop() grayscale = false;

  /**
   * Config properties; i.e. properties that may have configurable defaults.
   *
   * These properties **must be optional** and **must not have default values**.
   * Otherwise, the logic in `MeImage.config` does not make sense.
   *
   * @see MeImage.config
   * @see MeImage.defaults
   */
  @Prop() responsive?: boolean;
  @Prop() size?: 'contain' | 'cover';
  @Prop() round?: boolean;
  @Prop() highlighted?: boolean;
  @Prop() aspectRatio?: string;

  get config() {
    const config = {...this.defaults[this.variant]};
    config.responsive = this.responsive ?? config.responsive;
    config.size = this.size ?? config.size;
    config.round = this.round ?? config.round;
    config.highlighted = this.highlighted ?? config.highlighted;
    config.aspectRatio = this.aspectRatio ?? config.aspectRatio;
    return config;
  }

  private defaults = {
    'normal': {
      responsive: false,
      size: null,
      round: false,
      highlighted: false,
      aspectRatio: null,
    },
    'person': {
      responsive: false,
      size: 'cover',
      round: true,
      highlighted: true,
      aspectRatio: '1',
    },
  }

  private blurredBackground?: HTMLImageElement;

  private hasOverlaySlot = false;

  private get hasOverlay(): boolean {
    return Boolean(this.hasOverlaySlot || this.author);
  }

  private get overlayNeedsGradient(): boolean {
    return this.hasOverlaySlot;
  }

  componentWillLoad() {
    this.hasOverlaySlot = Boolean(this.host.querySelector(':scope *[slot=overlay]'));
  }

  render() {
    const classList = [
      'me-image',
      this.theme ? `me-image--${this.theme}`: null,
      this.authorSize ? `me-image--${this.authorSize}`: null,
      this.imageBlur ? `me-image--blur` : null,
      this.grayscale ? `me-image--grayscale` : null,
      this.config.responsive ? `me-image--responsive` : null,
      this.config.size ? `me-image--${this.config.size}` : null,
      this.config.round ? `me-image--round` : null,
      this.config.highlighted ? `me-image--highlighted` : null,
      this.hasOverlaySlot ? `me-image--has-overlay-slot` : null,
    ].filter(c => !!c).join(' ');

    const overlayClassList = [
      'me-image__overlay',
      this.overlayNeedsGradient ? 'me-image__overlay--with-gradient' : null,
    ].filter(c => !!c).join(' ');

    this.blurredBackground?.remove();

    if (this.imageBlur) {
      this.blurredBackground = this.host.querySelector('img').cloneNode() as HTMLImageElement;
      this.blurredBackground.classList.add('me-image__background-blur');
      this.host.prepend(this.blurredBackground);
    }

    this.host.style.aspectRatio = this.config.aspectRatio;
    this.host.querySelectorAll('img').forEach((image) => {
      image.style.aspectRatio = this.config.aspectRatio;
    });

    return (
      <Host class={classList}>
        <slot />
        {this.hasOverlay
          ? (
            <div class={overlayClassList}>
              <slot name="overlay" />
              {this.author
                ? (
                  <div class="me-image__author">
                    <span class="me-image__author-label">{this.author}</span>
                  </div>
                )
                : ''
              }
            </div>
          )
          : ''
        }
      </Host>
    );
  }
}
